import Page from 'components/util/Page';
import { Box, Card, Tab, Tabs } from '@mui/material';
import { useMemo } from 'react';
import { Button } from '@nps.today/component-library';
import Icon from 'components/icons/Icon';
import { useNavigate, useParams } from 'react-router';
import { TabContext, TabPanel } from '@mui/lab';
import { PATH_DASHBOARD } from 'routes/paths';
import ProductOrderList from './components/productOrder/ProductOrderList';
import BillableHoursOrderList from './components/billableHoursOrder/BillableHoursOrderList';
import { NoLicenseCard } from './components/NoLicenseCard';
import AccessGuard, { rules } from 'access';
import { useLicense } from 'hooks/useLicense';
import useAuth from 'hooks/useAuth';
import { useTranslator } from 'translation/useTranslator';

export enum ProductOrdersTab {
  PRODUCT_ORDERS = 'product_orders',
  BILLABLE_HOURS_ORDERS = 'billable_hours_orders'
}

const ProductOrders = () => {
  const translator = useTranslator();
  const navigate = useNavigate();
  const { tab = ProductOrdersTab.PRODUCT_ORDERS } = useParams();
  const { license, licenseLoading } = useLicense();
  const { selectedOrg } = useAuth();

  const TABS = useMemo(() => {
    return [
      {
        value: ProductOrdersTab.PRODUCT_ORDERS,
        label: translator.productOrders(),
        icon: <Icon type="integrationstore" />,
        component: <ProductOrderList />
      },
      {
        value: ProductOrdersTab.BILLABLE_HOURS_ORDERS,
        label: translator.billableHoursOrders(),
        icon: <Icon type="clock" />,
        component: <BillableHoursOrderList />
      }
    ];
  }, [translator]);

  const onTabChange = (newTab: ProductOrdersTab) => {
    navigate(`${PATH_DASHBOARD.productOrders.tabs}/${newTab}`);
  };

  const onAddOrder = () => {
    if (tab === ProductOrdersTab.PRODUCT_ORDERS) {
      navigate(PATH_DASHBOARD.productOrders.newProductOrder);
    }
    if (tab === ProductOrdersTab.BILLABLE_HOURS_ORDERS) {
      navigate(PATH_DASHBOARD.productOrders.newBillableHoursOrder);
    }
  };

  const addOrderText = useMemo(() => {
    if (tab === ProductOrdersTab.PRODUCT_ORDERS) {
      return translator.addProductOrder();
    }
    return translator.addBillableHoursOrder();
  }, [tab, translator]);

  return !license && !licenseLoading && selectedOrg?.organizationId ? (
    <Page title={translator.productOrders()}>
      <NoLicenseCard />
    </Page>
  ) : (
    <Page
      title={translator.productOrders()}
      stickyHeaderContent={[
        [
          <AccessGuard key="addOrderAccess" rule={rules.order.create}>
            <Button dataTestId="add-order" variant={'contained'} onClick={() => onAddOrder()}>
              {addOrderText}
            </Button>
          </AccessGuard>
        ]
      ]}
    >
      <Card sx={{ p: 3 }}>
        <TabContext value={tab}>
          <Tabs
            value={tab}
            scrollButtons="auto"
            variant="scrollable"
            allowScrollButtonsMobile
            onChange={(_, value) => onTabChange(value as ProductOrdersTab)}
          >
            {TABS.map((tab) => {
              return (
                <Tab
                  disableRipple
                  key={tab.label}
                  label={tab.label}
                  value={tab.value}
                  icon={tab.icon}
                />
              );
            })}
          </Tabs>
          <Box sx={{ mb: 3 }} />
          {TABS.map((tab) => (
            <TabPanel key={tab.label} value={tab.value}>
              {tab.component}
            </TabPanel>
          ))}
        </TabContext>
      </Card>
    </Page>
  );
};

export default ProductOrders;
